import { extractAliasValueFromProcessObject } from 'exacta-material-react/lib/src';
import { ExactaAPI } from 'exacta-material-react/lib/src/api/exactaapi';
import { END, eventChannel } from 'redux-saga';
import {
  actionChannel,
  all,
  call,
  delay,
  put,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import * as resourceActions from '../actions/resources.actions';

export const RESOURCE_LOADED_STATUS = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

let exactaAPI;

function readFile(response: any) {
  return eventChannel((emitter) => {
    if (response && response.response && response.response.data) {
      const reader = new FileReader();
      reader.addEventListener('loadend', (e: any) => {
        const elemValue = e.srcElement || e.target;
        const str = elemValue.result;
        emitter(str);
        emitter(END);
      });
      reader.readAsText(response.response.data);
    }
    return () => {
      console.debug('File reading completed');
    };
  });
}

function* getFile({
  payload,
  type,
  action,
  createPayload,
}: {
  payload: any;
  type: string;
  action: any;
  createPayload: (value: any) => void;
}) {
  exactaAPI = ExactaAPI.instance;
  const { fileDetails } = payload;
  const response = yield call(
    exactaAPI.download,
    fileDetails.value,
    fileDetails.fileName
  );
  const channel = yield call(readFile, response);
  try {
    while (true) {
      let fileContent = yield take(channel);
      yield put(
        action(createPayload ? createPayload(fileContent) : fileContent)
      );
    }
  } finally {
    console.debug('Resource file reading ended');
  }
}

const getFileReferenceField = (modelAlias: any) => {
  if (modelAlias === 'Window') {
    return 'WindowDefinition';
  } else if (modelAlias === 'TouchPanel') {
    return 'TouchPanelDefinition';
  } else if (modelAlias === 'Track') {
    return 'TrackDefinition';
  } else if (modelAlias === 'LayoutConfiguration') {
    return 'LayoutConfigurationDefinition';
  } else if (modelAlias === 'Trajectory') {
    return 'trajectoryDefinition';
  }
  return '';
};
const getContentTypeAlias = (objects: any) => {
  const contentType = extractAliasValueFromProcessObject(
    objects,
    'ContentType'
  );
  if (contentType && contentType.length > 0) {
    return contentType[0].alias;
  }
  return '';
};

const isVideoAsset = (contentType: string) => {
  return contentType === 'VIDEO';
};

const isImageAsset = (contentType: string) => {
  return contentType === 'IMAGE';
};

const isPdfAsset = (contentType: string) => {
  return contentType === 'PDF';
};

function getAssetResource({
  lessonId,
  objects,
  processInstance,
  resources,
  name,
}: {
  lessonId: string;
  objects: any;
  processInstance: any;
  resources: any;
  name: string;
}) {
  const container = extractAliasValueFromProcessObject(objects, 'Container');
  if (container.length === 0) {
    //Negative case where the asset content is empty
    return resourceActions.setExecutorResourcesForLesson({
      resourcePayload: {
        name,
        fileDetails: {},
      },
      lessonId,
    });
  }
  const embeddedId = resources[name].resourceId.split(':')[1];
  const embeddedProcess =
    processInstance.data[container[container.indexOf(embeddedId)]];
  const fileDetails = extractAliasValueFromProcessObject(
    embeddedProcess.objects,
    'Content'
  );
  const contentType = getContentTypeAlias(objects);
  if (
    isVideoAsset(contentType) ||
    isImageAsset(contentType) ||
    isPdfAsset(contentType)
  ) {
    //If the asset is a video, image or pdf just store the filedetails rather than content
    //We do not need the file content to be read as the these files can be directly showin through a url
    return resourceActions.setExecutorResourcesForLesson({
      resourcePayload: {
        name,
        fileDetails,
      },
      lessonId,
    });
  }
  return resourceActions.getFile(
    { fileDetails },
    resourceActions.setExecutorResourcesForLesson,
    (file) => ({
      resourcePayload: {
        name,
        fileDetails: file,
      },
      lessonId,
    })
  );
}

function getFileDetailsFromProcess(objects: any, modelAlias: string) {
  return extractAliasValueFromProcessObject(
    objects,
    getFileReferenceField(modelAlias)
  );
}

function getTrackResource({
  lessonId,
  objects,
  modelAlias,
  name,
}: {
  lessonId: string;
  objects: any;
  modelAlias: string;
  name: string;
}) {
  const trackIcon = extractAliasValueFromProcessObject(objects, 'TrackIcon');
  const fileDetails = getFileDetailsFromProcess(objects, modelAlias);
  return resourceActions.getFile(
    { fileDetails },
    resourceActions.setExecutorResourcesForLesson,
    (json) => ({
      resourcePayload: {
        name,
        fileDetails: { ...JSON.parse(json), trackIcon },
      },
      lessonId,
    })
  );
}

function getTrajectoryResource({
  lessonId,
  objects,
  modelAlias,
  name,
}: {
  lessonId: string;
  objects: any;
  modelAlias: string;
  name: string;
}) {
  const fileDetails = getFileDetailsFromProcess(objects, modelAlias);
  return resourceActions.getFile(
    { fileDetails },
    resourceActions.setExecutorResourcesForLesson,
    (json) => ({
      resourcePayload: {
        name,
        fileDetails: { ...JSON.parse(json) },
      },
      lessonId,
    })
  );
}

const getValueForKey = (objects: any, key: string) => {
  return extractAliasValueFromProcessObject(objects, key);
};

function createMapObject(objects: any) {
  const leftUpperCornerLonD = getValueForKey(objects, 'leftUpperCornerLonD');
  const leftUpperCornerLonM = getValueForKey(objects, 'leftUpperCornerLonM');
  const leftUpperCornerLonS = getValueForKey(objects, 'leftUpperCornerLonS');
  const leftUpperCornerLonH = getValueForKey(objects, 'leftUpperCornerLonH');
  const leftUpperCornerLatD = getValueForKey(objects, 'leftUpperCornerLatD');
  const leftUpperCornerLatM = getValueForKey(objects, 'leftUpperCornerLatM');
  const leftUpperCornerLatS = getValueForKey(objects, 'leftUpperCornerLatS');
  const leftUpperCornerLatH = getValueForKey(objects, 'leftUpperCornerLatH');
  const rightLowerCornerLonD = getValueForKey(objects, 'rightLowerCornerLonD');
  const rightLowerCornerLonM = getValueForKey(objects, 'rightLowerCornerLonM');
  const rightLowerCornerLonS = getValueForKey(objects, 'rightLowerCornerLonS');
  const rightLowerCornerLonH = getValueForKey(objects, 'rightLowerCornerLonH');
  const rightLowerCornerLatD = getValueForKey(objects, 'rightLowerCornerLatD');
  const rightLowerCornerLatM = getValueForKey(objects, 'rightLowerCornerLatM');
  const rightLowerCornerLatS = getValueForKey(objects, 'rightLowerCornerLatS');
  const rightLowerCornerLatH = getValueForKey(objects, 'rightLowerCornerLatH');
  const leftUpperCornerLon = `${leftUpperCornerLonD}° ${leftUpperCornerLonM}' ${leftUpperCornerLonS}" ${leftUpperCornerLonH}`;
  const leftUpperCornerLat = `${leftUpperCornerLatD}° ${leftUpperCornerLatM}' ${leftUpperCornerLatS}" ${leftUpperCornerLatH}`;
  const rightLowerCornerLon = `${rightLowerCornerLonD}° ${rightLowerCornerLonM}' ${rightLowerCornerLonS}" ${rightLowerCornerLonH}`;
  const rightLowerCornerLat = `${rightLowerCornerLatD}° ${rightLowerCornerLatM}' ${rightLowerCornerLatS}" ${rightLowerCornerLatH}`;
  return {
    mapImage: extractAliasValueFromProcessObject(objects, 'mapImage'),
    leftUpperCorner: [leftUpperCornerLon, leftUpperCornerLat],
    rightLowerCorner: [rightLowerCornerLon, rightLowerCornerLat],
  };
}

function createPayload({ processInstance }: { processInstance: any }) {
  if (processInstance.modelAlias === 'MapContainer') {
    const customObject: any = {
      mapByScale: {},
    };
    extractAliasValueFromProcessObject(
      processInstance.data.root.objects,
      'maps'
    ).forEach((embedId: any) => {
      const scale = extractAliasValueFromProcessObject(
        processInstance.data[embedId].objects,
        'mapScale'
      );
      console.log({ scale });
      customObject.mapByScale[scale] = createMapObject(
        processInstance.data[embedId].objects
      );
    });
    return customObject;
  } else if (processInstance.modelAlias === 'TrackSymbol') {
    return extractAliasValueFromProcessObject(
      processInstance.data.root.objects,
      'TrackSymbol'
    );
  }
  return processInstance;
}

function setProcessAsResource({
  processInstance,
  name,
}: {
  processInstance: any;
  name: string;
}) {
  return {
    type: 'SET_EXECUTOR_RESOURCES',
    payload: { name, fileDetails: createPayload({ processInstance }) },
  };
}

const processOnlyResources = [
  'MapContainer',
  'PredefinedDataItems',
  'TrackSymbol',
];
function* getResourcesForLesson({
  payload,
  type,
}: {
  payload: any;
  type: string;
}) {
  const { resources, lessonId } = payload;
  let ids: any[] = [];
  let idToValueMap: any = {};
  Object.keys(resources).forEach((key: string) => {
    if (resources[key].resourceType !== 'BasicFunction') {
      //Filtering out Basic function as player does not need it
      const processResourceId = resources[key].resourceId.split(':')[0];
      ids.push(processResourceId);
      idToValueMap[processResourceId] = key;
    }
  });
  const batchRequest = {
    type: 'processInstance',
    ids: ids,
  };
  const { response } = yield call(ExactaAPI.getInstance().get, batchRequest);
  //TODO Revisit this and change
  const processActions = response.data.payload
    .filter((item: any) => processOnlyResources.includes(item.modelAlias))
    .map((processInstance: any) => {
      const resourceName = idToValueMap[processInstance.id];
      return setProcessAsResource({ processInstance, name: resourceName });
    });
  yield all(processActions.map((ac: any) => put(ac)));
  const actions = response.data.payload
    .filter((item: any) => !processOnlyResources.includes(item.modelAlias))
    .map((processInstance: any) => {
      const {
        id,
        modelAlias,
        data: {
          root: { objects },
        },
      } = processInstance;
      const resourceName = idToValueMap[id];
      if (modelAlias === 'asset') {
        return getAssetResource({
          lessonId,
          objects,
          processInstance,
          resources,
          name: resourceName,
        });
      } else if (modelAlias === 'Track') {
        return getTrackResource({
          lessonId,
          objects,
          modelAlias,
          name: resourceName,
        });
      } else if (modelAlias === 'Trajectory') {
        return getTrajectoryResource({
          lessonId,
          objects,
          modelAlias,
          name: resourceName,
        });
      } else {
        const fileDetails = extractAliasValueFromProcessObject(
          objects,
          getFileReferenceField(modelAlias)
        );
        return resourceActions.getFile(
          { fileDetails },
          resourceActions.setExecutorResourcesForLesson,
          (json) => ({
            resourcePayload: {
              name: resourceName,
              fileDetails: { ...JSON.parse(json) },
            },
            lessonId,
          })
        );
      }
    });
  yield put(
    resourceActions.waitForResources({ total: actions.length, lessonId })
  );
  yield all(actions.map((ac: any) => put(ac)));
}

function* waitForLoadingResourceCompletion({
  payload,
  type,
}: {
  payload: any;
  type: string;
}) {
  let count = 0;
  const { total, lessonId } = payload;
  try {
    while (count < total) {
      const action = yield take(
        resourceActions.types.RESOURCE_LOADED_FOR_LESSON
      );
      if (lessonId === action.payload) {
        count++;
      }
    }
    yield put(resourceActions.resourceLoadingForLessonSuccess(lessonId));
  } catch (error) {
    yield put(resourceActions.resourceLoadingForLessonFailed(lessonId));
  }
}

function* getLessonDefinition({
  type,
  payload,
}: {
  type: string;
  payload: any;
}) {
  const { trainingUnit, version } = payload;
  const trainingUnitData = trainingUnit.data;
  const fileDetails = trainingUnitData[`${version}`].objects.Definition.content;
  yield put(
    resourceActions.getFile(
      { fileDetails },
      resourceActions.setExecutorResources,
      (json) => {
        const {
          content: {
            graph,
            layout,
            requiredComponents,
            roles,
            mfcc,
            version,
            lessonId,
            title,
            estimatedTime,
          },
        } = JSON.parse(json);
        const definitionFromFile = {
          version: {
            number: {
              major: Number(version.number.major),
              minor: Number(version.number.minor),
              patch: 0, //Remove this once schema is fixed
            },
            date: version.date,
          },
          id: lessonId,
          estimatedTime,
          title,
          graph,
          layout,
          requiredComponents,
          roles,
          mfcc,
        };
        return {
          name: 'lessonDefinition',
          fileDetails: definitionFromFile,
        };
      }
    )
  );
}

function* setExecutorResourcesForLessonSaga({
  type,
  payload,
}: {
  type: string;
  payload: any;
}) {
  console.log({ payload });
  const { resourcePayload, lessonId } = payload;
  yield put(resourceActions.setExecutorResources(resourcePayload));
  yield put(resourceActions.resourceLoadedForLesson(lessonId));
}

const actionWatcher = function* () {
  yield takeEvery(
    resourceActions.types.GET_RESOURCES_FOR_LESSON,
    getResourcesForLesson
  );
  yield takeEvery(
    resourceActions.types.GET_LESSON_DEFINITION,
    getLessonDefinition
  );
  yield takeEvery(resourceActions.types.GET_FILE, getFile);
  yield takeEvery(
    resourceActions.types.WAIT_FOR_RESOURCES,
    waitForLoadingResourceCompletion
  );
  yield takeEvery(
    resourceActions.types.SET_EXECUTOR_RESOURCES_FOR_LESSON,
    setExecutorResourcesForLessonSaga
  );
};

export default actionWatcher;
